import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyFormIdContract } from "shared/api/contracts/society/societyId/forms/formId";
import { getSocietyProfileReportIdContract } from "shared/api/contracts/society/societyId/profiles/profileId/reports/reportId";
import Spinner from "shared/components/common/Spinner";
import { usePillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type ReviewInstanceAssignmentView } from "shared/mappers/database/review/reviewInstanceAssignment";
import { type HubReviewsRouteParams } from "shared/types/HubPageRouteParams";
import { HubReviewsRecusalModal } from "hub/src/components/reviews/HubReviewsRecusalModal";
import { HubReviewsVotingNavigationButtons } from "hub/src/components/reviews/HubReviewsVotingNavigationButtons";
import { HubReviewsVotingPageTabs } from "hub/src/components/reviews/HubReviewsVotingPageTabs";
import { HubReviewsVotingScorecardForm } from "hub/src/components/reviews/HubReviewsVotingScorecardForm";
import { useHubReviewsVotingTabsArray } from "hub/src/components/reviews/useHubReviewsVotingTabsArray";
import { useHubReviewsContext } from "hub/src/context/reviews/HubReviewsContext";
import { HubReviewsVotingPageContext } from "hub/src/context/reviews/HubReviewsVotingPageContext";
import { type HubRouteConfig } from "hub/src/routes/RouteConfigWrapper";

const HubReviewsVotingPage = (
  routeConfig: HubRouteConfig<HubReviewsRouteParams>,
) => {
  const session = useCustomSession();
  const recusalModalControl = usePillarModalControl();
  const { reviewSessions, setCurrentInstanceId } = useHubReviewsContext({
    reviewInstanceReportProfileId:
      routeConfig.routeParams?.reviewInstanceReportProfileId,
    reviewSessionReportProfileId:
      routeConfig.routeParams?.reviewSessionReportProfileId,
    singleReviewInstanceReportProfileId:
      routeConfig.routeParams?.singleReviewInstanceReportProfileId,
  });

  const { reviewInstanceId, reviewSessionId } = routeConfig.routeParams!;

  const [selectedTab, setSelectedTab] = useState<string>("tab-0");

  const currentReviewSession = reviewSessions?.find(
    (s) => s.id === reviewSessionId,
  );

  const { data: formDesign, isLoading: formDesignIsLoading } = useQuery({
    queryKey: ["scoreCardForm", currentReviewSession?.scorecardForm?.formId],
    queryFn: async () => {
      if (!reviewSessionId || !reviewInstanceId || !session.profileId) {
        return null;
      }

      return await apiRequest({
        contract: getSocietyFormIdContract,
        inputs: {
          params: {
            societyId: session.societyId!,
            formId: currentReviewSession!.scorecardForm!.formId,
          },
        },
      });
    },
    enabled: !!currentReviewSession?.scorecardForm?.formId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: reviewInstanceAssignment,
    isFetching: reviewInstanceAssignmentIsFetching,
  } = useQuery<ReviewInstanceAssignmentView | null>({
    queryKey: [
      "reviewInstanceAssignment",
      reviewSessionId,
      reviewInstanceId,
      session.profileId,
    ],
    queryFn: async () => {
      if (!reviewSessionId || !reviewInstanceId || !session.profileId) {
        return null;
      }

      const response = await apiRequest({
        contract: getSocietyProfileReportIdContract,
        inputs: {
          params: {
            societyId: session.societyId!,
            reportId:
              routeConfig.routeParams!.singleReviewInstanceReportProfileId!,
            profileId: session.profileId!,
          },
        },
        filters: {
          reviewInstanceId,
        },
      });

      const currentAssignment = response
        ?.results[0] as unknown as ReviewInstanceAssignmentView;

      setCurrentInstanceId(currentAssignment?.reviewInstance?.reviewInstanceId);

      return currentAssignment;
    },
    enabled: !!(
      reviewSessionId &&
      reviewInstanceId &&
      session.profileId &&
      reviewSessions?.length
    ),
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    setSelectedTab("tab-0");
  }, [reviewInstanceAssignment?.reviewInstanceAssignmentId]);

  const tabs = useHubReviewsVotingTabsArray(
    reviewInstanceAssignment ?? null,
    currentReviewSession,
  );

  if (!reviewInstanceAssignment || reviewInstanceAssignmentIsFetching) {
    return <Spinner />;
  }

  return (
    <HubReviewsVotingPageContext.Provider
      value={{
        reviewSession: currentReviewSession!,
        reviewInstanceAssignment,
        reviewInstanceAssignmentIsFetching,
        recusalModalControl,
        nextInstanceId: currentReviewSession?.progress?.nextInstanceId,
        previousInstanceId: currentReviewSession?.progress?.previousInstanceId,
        formDesign: formDesign!,
        formDesignIsLoading,
        tabs,
        selectedTab,
        setSelectedTab,
      }}
    >
      <HubReviewsRecusalModal />
      <div className="p-5 h-full overflow-y-auto">
        <div className="grid grid-cols-7">
          <HubReviewsVotingPageTabs />
          <div className="col-span-3">
            <HubReviewsVotingNavigationButtons />
            <HubReviewsVotingScorecardForm
              key={reviewInstanceAssignment.reviewInstanceAssignmentId}
            />
          </div>
        </div>
      </div>
    </HubReviewsVotingPageContext.Provider>
  );
};

export default HubReviewsVotingPage;
